import { z } from "zod";

import { listQuerySchema } from "../common/list-query";
import { paginatedSchema } from "../common/pagination";
import { tenantScopeSchema } from "../common/tenant";

/**
 * Lending equipment and getting it back: the *comodato*.
 *
 * The whole shape of this contract comes from one sentence of Argentine law.
 * Article 1533 of the Código Civil y Comercial defines a comodato as handing
 * over a non-fungible thing so the other party may use it **gratuitously**
 * and return the same thing. So there is no price anywhere in this file, and
 * that is a decision rather than an omission: the monthly fee is
 * consideration for the monitoring service and belongs to a different
 * document. A fee for the *use of the equipment* would make this a lease, and
 * a lease does not carry article 1536(d), under which the borrower answers
 * for loss or deterioration even by chance (ADR-074).
 *
 * Nothing here is legally reviewed. The printed document says so, and it
 * keeps saying so until a qualified lawyer clears the wording.
 */

export const LOAN_AGREEMENT_STATUSES = [
  "DRAFT",
  "ISSUED",
  "SIGNED",
  "CLOSED",
  "CANCELLED",
] as const;
export const loanAgreementStatusSchema = z.enum(LOAN_AGREEMENT_STATUSES);
export type LoanAgreementStatus = z.infer<typeof loanAgreementStatusSchema>;

export const LOAN_ITEM_OUTCOMES = ["RETURNED", "RETURNED_DAMAGED", "LOST", "CONTINUED"] as const;
export const loanItemOutcomeSchema = z.enum(LOAN_ITEM_OUTCOMES);
export type LoanItemOutcome = z.infer<typeof loanItemOutcomeSchema>;

/** Outcomes an operator may enter when recording a physical return. */
export const PHYSICAL_LOAN_ITEM_OUTCOMES = ["RETURNED", "RETURNED_DAMAGED", "LOST"] as const;
export const physicalLoanItemOutcomeSchema = z.enum(PHYSICAL_LOAN_ITEM_OUTCOMES);
export type PhysicalLoanItemOutcome = z.infer<typeof physicalLoanItemOutcomeSchema>;

export const LEGAL_PERSON_KINDS = ["HUMAN", "LEGAL_ENTITY"] as const;
export const legalPersonKindSchema = z.enum(LEGAL_PERSON_KINDS);
export type LegalPersonKind = z.infer<typeof legalPersonKindSchema>;

/** Which states still allow the terms to be edited. */
export const EDITABLE_STATUSES: readonly LoanAgreementStatus[] = ["DRAFT"];

/**
 * The default term, in months.
 *
 * Three years, matching the term the tariff is built on (ADR-068). Worth
 * knowing which way it cuts: article 1536(e) lets the lender demand the thing
 * back at any time when no term is agreed, and article 1539 narrows early
 * return to an unforeseen and urgent need or a use other than the one agreed.
 * Writing a term down restricts us, not them.
 */
export const DEFAULT_LOAN_TERM_MONTHS = 36;

/**
 * One numbered clause of the printed instrument.
 *
 * Rendered from a template at issue and then stored, so rewording the
 * template never rewrites a contract somebody already signed. `body` is
 * plain text with blank lines between paragraphs; no markup, because the
 * only thing that renders it is a printed page.
 *
 * `title` / `body` are Spanish and **controlling**. Optional `titleEn` /
 * `bodyEn` are a courtesy English translation for bilingual print; if they
 * conflict, Spanish prevails (ADR-076 / ADR-074).
 */
export const loanClauseSchema = z.object({
  title: z.string(),
  body: z.string(),
  titleEn: z.string().optional(),
  bodyEn: z.string().optional(),
});

export type LoanClause = z.infer<typeof loanClauseSchema>;

export const loanAgreementItemSchema = z.object({
  id: z.string(),
  deviceId: z.string(),
  /** Copied at issue, so a renamed or deleted device cannot alter the document. */
  devEui: z.string(),
  manufacturer: z.string(),
  model: z.string(),
  returnedOn: z.iso.datetime().nullable(),
  outcome: loanItemOutcomeSchema.nullable(),
  outcomeNotes: z.string().nullable(),
});

export type LoanAgreementItem = z.infer<typeof loanAgreementItemSchema>;

export const loanAgreementSchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  organizationName: z.string(),
  number: z.string(),
  status: loanAgreementStatusSchema,

  lenderName: z.string(),
  lenderTaxId: z.string(),
  lenderAddress: z.string(),

  borrowerKind: legalPersonKindSchema,
  borrowerName: z.string(),
  borrowerTaxId: z.string(),
  borrowerAddress: z.string(),

  signatoryName: z.string(),
  signatoryDocument: z.string(),
  signatoryCapacity: z.string(),

  siteId: z.string().nullable(),
  siteName: z.string(),
  siteAddress: z.string(),

  purpose: z.string(),
  termMonths: z.number().int(),
  startsOn: z.iso.datetime(),
  endsOn: z.iso.datetime(),

  /** The date on the paper, in the customer's hand. */
  signedOn: z.iso.datetime().nullable(),
  /** When one of ours recorded having seen that paper, and who. */
  attestedAt: z.iso.datetime().nullable(),
  attestedByEmail: z.string().nullable(),

  issuedAt: z.iso.datetime().nullable(),
  closedAt: z.iso.datetime().nullable(),
  cancelledAt: z.iso.datetime().nullable(),

  notes: z.string().nullable(),
  items: z.array(loanAgreementItemSchema),

  /** Set when this draft was drawn as a renewal of another agreement (ADR-076). */
  renewedFromId: z.string().nullable(),

  /** Empty while the agreement is a draft: nothing has been frozen yet. */
  clauses: z.array(loanClauseSchema),
  templateVersion: z.string().nullable(),
  /**
   * Shown on every copy until a matriculated lawyer clears the wording. The
   * server decides this, not the client, so a frontend cannot print a clean
   * copy of an unreviewed contract by omitting a flag.
   */
  draftNotice: z.string().nullable(),
  /**
   * Footer on every printable copy: this file is for the expediente, not a
   * digital signature (ADR-076). Server-attached like `draftNotice`.
   */
  fileCopyNotice: z.string().nullable(),

  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export type LoanAgreement = z.infer<typeof loanAgreementSchema>;

export const loanAgreementListSchema = paginatedSchema(loanAgreementSchema);
export type LoanAgreementList = z.infer<typeof loanAgreementListSchema>;

export const listLoanAgreementsQuerySchema = listQuerySchema.extend({
  status: loanAgreementStatusSchema.optional(),
  siteId: z.uuid().optional(),
  /**
   * Live agreements (issued or signed) whose end date is on or before today
   * plus this many days — including ones already overdue. Used to chase
   * renewals (ADR-076).
   */
  endingWithinDays: z.coerce.number().int().min(0).max(365).optional(),
});

export type ListLoanAgreementsQuery = z.infer<typeof listLoanAgreementsQuerySchema>;

const identityFields = {
  borrowerKind: legalPersonKindSchema,
  borrowerName: z.string().trim().min(1).max(200),
  borrowerTaxId: z.string().trim().min(1).max(40),
  borrowerAddress: z.string().trim().min(1).max(300),
  signatoryName: z.string().trim().min(1).max(200),
  signatoryDocument: z.string().trim().min(1).max(40),
  /**
   * "Titular", "apoderado", "socio gerente". Free text because the list of
   * ways somebody can be entitled to sign is not ours to enumerate, and
   * article 1535(b) makes the answer worth printing rather than assuming.
   */
  signatoryCapacity: z.string().trim().min(1).max(120),
};

export const createLoanAgreementRequestSchema = z.object({
  ...tenantScopeSchema.shape,
  ...identityFields,
  siteId: z.uuid(),
  purpose: z.string().trim().min(1).max(500),
  termMonths: z.number().int().min(1).max(120).default(DEFAULT_LOAN_TERM_MONTHS),
  startsOn: z.iso.date(),
  /** DevEUIs are not accepted here: a device is named by its own id. */
  deviceIds: z.array(z.uuid()).min(1).max(50),
  notes: z.string().trim().max(2_000).optional(),
});

export type CreateLoanAgreementRequest = z.infer<typeof createLoanAgreementRequestSchema>;

export const updateLoanAgreementRequestSchema = z.object({
  borrowerKind: legalPersonKindSchema.optional(),
  borrowerName: identityFields.borrowerName.optional(),
  borrowerTaxId: identityFields.borrowerTaxId.optional(),
  borrowerAddress: identityFields.borrowerAddress.optional(),
  signatoryName: identityFields.signatoryName.optional(),
  signatoryDocument: identityFields.signatoryDocument.optional(),
  signatoryCapacity: identityFields.signatoryCapacity.optional(),
  purpose: z.string().trim().min(1).max(500).optional(),
  termMonths: z.number().int().min(1).max(120).optional(),
  startsOn: z.iso.date().optional(),
  deviceIds: z.array(z.uuid()).min(1).max(50).optional(),
  notes: z.string().trim().max(2_000).optional(),
});

export type UpdateLoanAgreementRequest = z.infer<typeof updateLoanAgreementRequestSchema>;

/**
 * Recording that the customer signed the paper.
 *
 * `signedOn` is the date written on the document, which is the customer's
 * fact; the platform separately records when one of ours entered it, which is
 * ours. They are not the same date and the document prints both, because
 * quietly stamping our clock on somebody else's signature is the kind of
 * detail that loses an argument later.
 *
 * This is **not a signature**. Ley 25.506 gives the presumptions of authorship
 * and integrity only to a digital signature from a licensed certifier, and its
 * article 5 puts the burden of proving a disowned electronic signature on
 * whoever invokes it. What is stored here is an attestation by a named member
 * of staff that a signed paper exists (ADR-074).
 */
export const attestSignatureRequestSchema = z.object({
  signedOn: z.iso.date(),
  notes: z.string().trim().max(500).optional(),
});

export type AttestSignatureRequest = z.infer<typeof attestSignatureRequestSchema>;

export const recordReturnRequestSchema = z.object({
  deviceId: z.uuid(),
  returnedOn: z.iso.date(),
  outcome: physicalLoanItemOutcomeSchema,
  notes: z.string().trim().max(500).optional(),
});

export type RecordReturnRequest = z.infer<typeof recordReturnRequestSchema>;

/** Whether every unit on the agreement has been accounted for. */
export function allItemsSettled(items: readonly LoanAgreementItem[]): boolean {
  return items.length > 0 && items.every((item) => item.outcome !== null);
}

/** Units still out, which is the number that matters when chasing a return. */
export function outstandingItems(items: readonly LoanAgreementItem[]): number {
  return items.filter((item) => item.outcome === null).length;
}
